import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Target, Eye, Users, Shield, Award, Leaf } from 'lucide-react';

export const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Shield,
      title: t('values.trust.title'),
      text: t('values.trust.text'),
    },
    {
      icon: Award,
      title: t('values.expertise.title'),
      text: t('values.expertise.text'),
    },
    {
      icon: Target,
      title: t('values.solution.title'),
      text: t('values.solution.text'),
    },
    {
      icon: Leaf,
      title: t('values.sustainability.title'),
      text: t('values.sustainability.text'),
    },
  ];

  return (
    <div data-testid="about-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 md:pb-32 bg-[#0F1A2E]">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-30">
          <img
            src="https://images.unsplash.com/photo-1664162165248-aa5432d1a9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmclMjBzaWxob3VldHRlJTIwZ2xhc3MlMjB3YWxsfGVufDB8fHx8MTc3MDY3MTYwMXww&ixlib=rb-4.1.0&q=85"
            alt="Business"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A2E] via-[#0F1A2E]/80 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-6">
              {t('about.title')}
            </p>
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-none mb-8">
              <span className="font-heading">{t('about.subtitle')}</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl leading-relaxed">
              {t('about.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-[#C9A84C]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center border-l-2 border-[#C9A84C]/30 pl-4"
            >
              <p className="font-heading text-xl md:text-2xl font-semibold text-[#C9A84C]">{t('stats.brokerage.title')}</p>
              <p className="text-[#1B2B4B]/60 mt-2">{t('stats.brokerage.subtitle')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center border-l-2 border-[#C9A84C]/30 pl-4"
            >
              <p className="font-heading text-xl md:text-2xl font-semibold text-[#C9A84C]">{t('stats.energy.title')}</p>
              <p className="text-[#1B2B4B]/60 mt-2">{t('stats.energy.subtitle')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center border-l-2 border-[#C9A84C]/30 pl-4"
            >
              <p className="font-heading text-xl md:text-2xl font-semibold text-[#C9A84C]">{t('stats.analysis.title')}</p>
              <p className="text-[#1B2B4B]/60 mt-2">{t('stats.analysis.subtitle')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="mission-section"
              className="bg-white p-10 relative overflow-hidden shadow-lg border border-[#C9A84C]/20"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A84C]/10 rounded-full blur-3xl" />
              <Target className="text-[#C9A84C] mb-6" size={48} />
              <h2 className="font-heading text-3xl font-bold text-[#1B2B4B] mb-6">
                {t('about.mission.title')}
              </h2>
              <p className="text-[#1B2B4B]/70 leading-relaxed text-lg">
                {t('about.mission.text')}
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="vision-section"
              className="bg-white p-10 relative overflow-hidden shadow-lg border border-[#C9A84C]/20"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A84C]/10 rounded-full blur-3xl" />
              <Eye className="text-[#C9A84C] mb-6" size={48} />
              <h2 className="font-heading text-3xl font-bold text-[#1B2B4B] mb-6">
                {t('about.vision.title')}
              </h2>
              <p className="text-[#1B2B4B]/70 leading-relaxed text-lg">
                {t('about.vision.text')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              {t('values.title')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1B2B4B]">
              {t('values.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`value-card-${index}`}
                className="bg-[#F8F6F3] border border-[#1B2B4B]/10 p-8 hover:border-[#C9A84C] hover:shadow-lg transition-all duration-500 text-center"
              >
                <value.icon className="text-[#C9A84C] mb-6 mx-auto" size={40} />
                <h3 className="font-heading text-xl font-semibold text-[#1B2B4B] mb-4">
                  {value.title}
                </h3>
                <p className="text-[#1B2B4B]/60 leading-relaxed text-sm">
                  {value.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section - Biz Kimiz */}
      <section data-testid="team-section" className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              Ekibimiz
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1B2B4B]">
              Biz Kimiz
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Yılmaz Uzuner */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid="team-member-1"
              className="bg-white border border-[#1B2B4B]/10 overflow-hidden hover:shadow-xl transition-all duration-500"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_8bd884c3-97d2-46df-a395-cdae7e063ef0/artifacts/ov43oi9f_file.jpg"
                  alt="Yılmaz Uzuner"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-bold text-[#1B2B4B] mb-2">
                  Yılmaz Uzuner
                </h3>
                <p className="text-[#C9A84C] text-sm font-medium mb-4">Kurucu / Finansal Danışman</p>
                <p className="text-[#1B2B4B]/70 text-sm leading-relaxed">
                  Bankacılık ve finans alanında toplam 25 yıllık profesyonel deneyime sahip bir finansal danışmandır. Bunun 22 yılını aktif bankacılık, 3 yılını ise finansal danışmanlık süreçlerinde geçirmiş; özellikle rasyo ve bilanço analizi, krediye erişim, enerji iş geliştirme ile gemicilik ve enerji finansmanı alanlarında uzmanlaşmıştır. Kariyeri boyunca şirketlerin finansal yapılarının güçlendirilmesi, doğru finansman kaynaklarına yönlendirilmesi ve sürdürülebilir büyüme stratejilerinin oluşturulması konularında çalışmıştır. Bugün BEU Danışmanlık bünyesinde, kurumlara veri odaklı, uygulanabilir ve sonuç üreten finansal danışmanlık hizmetleri sunmaktadır.
                </p>
              </div>
            </motion.div>

            {/* Burcu Kanatlı */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="team-member-2"
              className="bg-white border border-[#1B2B4B]/10 overflow-hidden hover:shadow-xl transition-all duration-500"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_finance-hub-610/artifacts/cyn2pp70_burcu%20kanatl%C4%B1.jpg"
                  alt="Burcu Kanatlı"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h3 className="font-heading text-2xl font-bold text-[#1B2B4B] mb-2">
                  Burcu Kanatlı
                </h3>
                <p className="text-[#C9A84C] text-sm font-medium mb-4">Finansal Danışman</p>
                <p className="text-[#1B2B4B]/70 text-sm leading-relaxed">
                  Proje finansmanı, kurumsal krediler ve yapılandırılmış finansman alanlarında 10 yılı aşkın bankacılık deneyimine sahip bir finansal danışmandır. Önde gelen finans kurumlarında enerji ve altyapı başta olmak üzere sermaye yoğun sektörlerde finansal modelleme, kredi analizi, risk yönetimi ve yapılandırılmış finansman üzerinde çalışmıştır. Bugün şirketlere veri odaklı, sürdürülebilir ve uluslararası finansman standartlarına uyumlu finansal danışmanlık hizmetleri sunmaktadır.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 md:py-32 bg-[#1B2B4B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80"
                alt="Corporate"
                className="w-full h-80 object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img
                src="https://images.unsplash.com/photo-1631088902967-8bc33c083d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZXMlMjBzdW5zZXQlMjBkYXJrfGVufDB8fHx8MTc3MDY3MTU4M3ww&ixlib=rb-4.1.0&q=85"
                alt="Energy"
                className="w-full h-80 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
